import React, { useId } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  space,
  spacing,
  status,
  transition,
  typography,
} from '../../style-utils/theme';
import { VisuallyHidden } from '../../style-utils/text';
import { Label } from '../form-kit';

/**
 * A TEXTAREA THAT SHOWS ITS LIMIT — always, not only once it is broken.
 *
 * The counter row is the text editor's (styled-quill/text-editor.tsx): "n / max"
 * under the field, muted until it matters. Past the limit it turns
 * `status.alert.fg`, NOT `palette.alert` — coral as small text is 3.42:1 and
 * fails, the same defect the IQ chat counter shipped with.
 *
 * NOT capped with maxLength. A hard cap silently eats the end of a paste, so
 * the writer never learns what was lost; here they can go over, see by how
 * much, and trim it themselves. The field is marked aria-invalid while over,
 * and the form decides whether to refuse the save.
 *
 * The counter is tied to the field by aria-describedby (merged with any id the
 * caller already passes, e.g. a FieldHint), so a screen reader hears
 * "12 / 280 characters" — or how far over — when it reaches the field. It is
 * not a live region: announcing every keystroke is unusable.
 *
 * `id` is forwarded to the textarea (one is generated when left out) so a
 * form-kit Label elsewhere can point at it; pass `label` to have one drawn here.
 */

export interface CountedTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'maxLength'> {
  value: string;
  /** The limit the counter measures against. */
  max: number;
  /** A visible label, linked to the field. Left out, the caller labels it. */
  label?: React.ReactNode;
  hasError?: boolean;
}

export const CountedTextarea = React.forwardRef<HTMLTextAreaElement, CountedTextareaProps>(
  (
    {
      value,
      max,
      label,
      hasError = false,
      id,
      rows = 4,
      'aria-describedby': describedBy,
      'aria-invalid': ariaInvalid,
      ...rest
    },
    ref
  ) => {
    const uid = useId();
    const fieldId = id ?? `${uid}-field`;
    const counterId = `${fieldId}-count`;
    const length = value.length;
    const over = length > max;
    const invalid = hasError || over || ariaInvalid === true || ariaInvalid === 'true';

    return (
      <Wrap>
        {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
        <Area
          ref={ref}
          id={fieldId}
          value={value}
          rows={rows}
          aria-describedby={[describedBy, counterId].filter(Boolean).join(' ')}
          aria-invalid={invalid || undefined}
          $invalid={invalid}
          {...rest}
        />
        <Counter id={counterId} $over={over} data-over={over ? 'true' : undefined}>
          {length} / {max}
          <VisuallyHidden>
            {over ? ` characters, ${length - max} over the limit` : ' characters'}
          </VisuallyHidden>
        </Counter>
      </Wrap>
    );
  }
);
CountedTextarea.displayName = 'CountedTextarea';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

type AreaProps = { $invalid: boolean };

// The same four states as the form-kit Input: resting, hover, focus, error.
const areaBorder = (p: AreaProps) => {
  return p.$invalid ? palette.alert : palette.line;
};
const areaShadow = (p: AreaProps) => {
  return p.$invalid ? shadow.focusError : shadow.xs;
};
const areaHoverBorder = (p: AreaProps) => {
  return p.$invalid ? palette.alert : palette.lineStrong;
};
const areaFocusBorder = (p: AreaProps) => {
  return p.$invalid ? palette.alert : palette.iris;
};
const areaFocusShadow = (p: AreaProps) => {
  return p.$invalid ? shadow.focusError : shadow.focus;
};

const Area = styled.textarea<AreaProps>`
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: ${space(12)};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${areaBorder};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  box-shadow: ${areaShadow};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  resize: vertical;
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:hover {
    border-color: ${areaHoverBorder};
  }
  &:focus {
    outline: none;
    border-color: ${areaFocusBorder};
    box-shadow: ${areaFocusShadow};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${palette.slateDisabled};
    background: ${palette.surfaceMuted};
  }
`;

type CounterProps = { $over: boolean };

const counterColor = (p: CounterProps) => {
  return p.$over ? status.alert.fg : palette.slateLight;
};

const Counter = styled.div<CounterProps>`
  position: relative;
  margin-top: ${spacing.half};
  text-align: right;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.snug};
  font-variant-numeric: tabular-nums;
  color: ${counterColor};
`;
